import React, { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, XCircle, Send, Loader2, MessageSquare } from 'lucide-react';
import InternationalPhoneInput from '@/components/ui/InternationalPhoneInput';
import { Helmet } from 'react-helmet-async';

const SmsTest: React.FC = () => {
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('Hello from Resonow! This is a test SMS message. 🌿');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleSend = async () => {
    if (!phone || !message) return;

    setIsLoading(true);
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke('send-sms', {
        body: { to: phone, message }
      });

      if (error) throw error;

      if (data?.success) {
        setResult({
          success: true,
          message: `SMS sent successfully! Message ID: ${data.messageId || 'N/A'}`
        });
      } else {
        setResult({
          success: false,
          message: data?.error || 'Failed to send SMS'
        });
      }
    } catch (err: any) {
      setResult({
        success: false,
        message: err.message || 'Unexpected error occurred'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>SMS Test | Resonow Admin</title>
      </Helmet>
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              Bird SMS Test
            </CardTitle>
            <CardDescription>
              Send a test SMS message via Bird API to verify the integration.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number (E.164 format)</Label>
              <InternationalPhoneInput
                id="phone"
                value={phone}
                onChange={setPhone}
                placeholder="+16505551234"
              />
              <p className="text-xs text-muted-foreground">
                Must include country code (e.g., +1 for US/CA, +44 for UK, +86 for CN)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                maxLength={1600}
                placeholder="Enter your test message..."
              />
              <p className="text-xs text-muted-foreground text-right">
                {message.length}/1600 characters
              </p>
            </div>

            <Button
              onClick={handleSend}
              disabled={isLoading || !phone || !message}
              className="w-full"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Send SMS
                </>
              )}
            </Button>

            {result && (
              <Alert variant={result.success ? 'default' : 'destructive'} className={result.success ? 'border-primary/30 bg-primary/5' : ''}>
                {result.success ? (
                  <CheckCircle className="h-4 w-4 text-primary" />
                ) : (
                  <XCircle className="h-4 w-4" />
                )}
                <AlertDescription>
                  {result.message}
                </AlertDescription>
              </Alert>
            )}

            <div className="rounded-md bg-muted p-3 text-xs text-muted-foreground space-y-1">
              <p className="font-medium">Integration Details</p>
              <p>• API: Bird (MessageBird) REST API</p>
              <p>• Key stored as: <code className="bg-background px-1 rounded">BIRD_API_KEY</code></p>
              <p>• Sender stored as: <code className="bg-background px-1 rounded">BIRD_PHONE_NUMBER</code></p>
              <p>• Edge function: <code className="bg-background px-1 rounded">send-sms</code></p>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default SmsTest;
